"use client";

import { useState, useCallback } from "react";
import { Upload, FileText, Loader2, CheckCircle, AlertCircle, Clock, XCircle } from "lucide-react";
import { formatDate } from "@/lib/formatting";
import { useTenant } from "@/lib/tenant";
import type { DealVerification } from "@/lib/deals/verification";
import type { IdentityStatus } from "@/lib/deals/types";
interface IdentityUploadProps {
  dealId: string;
  role: "seller" | "buyer";
  verification: DealVerification | null;
  onUploadComplete?: (verification: DealVerification) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * IdentityUpload - Component for buyers/sellers to upload ID documents
 *
 * Mirrors the OkokontoUpload pattern but stores identity documents
 * per-role (buyer_identity_* / seller_identity_*).
 */
export function IdentityUpload({
  dealId,
  role,
  verification,
  onUploadComplete,
  disabled = false,
  className = ""
}: IdentityUploadProps) {
  const tenant = useTenant();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const prefix = role === "seller" ? "seller_identity" : "buyer_identity";
  const documentUrl = verification?.[`${prefix}_document_url` as keyof DealVerification] as string | null;
  const documentName = verification?.[`${prefix}_document_name` as keyof DealVerification] as string | null;
  const uploadedAt = verification?.[`${prefix}_uploaded_at` as keyof DealVerification] as string | null;
  const status = verification?.[`${prefix}_status` as keyof DealVerification] as IdentityStatus ?? "pending";
  const hasDocument = !!documentUrl;
  const canUpload = !disabled && (!hasDocument || status === "rejected");
  const handleUpload = useCallback(async (file: File) => {
    if (!file) return;
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Bitte laden Sie eine PDF- oder Bilddatei hoch.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("Die Datei ist zu groß. Maximale Größe: 10 MB.");
      return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("role", role);
      const response = await fetch(`/api/deals/${dealId}/identity/upload`, {
        method: "POST",
        body: formData
      });
      if (!response.ok) {
        const error_0 = await response.json();
        throw new Error(error_0.error || "Upload fehlgeschlagen");
      }
      const data = await response.json();
      onUploadComplete?.(data.data);
    } catch (error) {
      console.error("[IdentityUpload] Upload error:", error);
      setUploadError(error instanceof Error ? error.message : "Upload fehlgeschlagen");
    } finally {
      setIsUploading(false);
    }
  }, [dealId, role, onUploadComplete]);
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (!canUpload) return;
    const file_0 = e.dataTransfer.files[0];
    if (file_0) handleUpload(file_0);
  }, [canUpload, handleUpload]);
  const handleDragOver = useCallback((e_0: React.DragEvent<HTMLDivElement>) => {
    e_0.preventDefault();
    setDragActive(true);
  }, []);
  const handleDragLeave = useCallback((e_1: React.DragEvent<HTMLDivElement>) => {
    e_1.preventDefault();
    setDragActive(false);
  }, []);
  const handleFileInput = useCallback((e_2: React.ChangeEvent<HTMLInputElement>) => {
    const file_1 = e_2.target.files?.[0];
    if (file_1) handleUpload(file_1);
  }, [handleUpload]);

  // Show existing document when uploaded/verified
  if (hasDocument && status !== "rejected") {
    return <div className={`rounded-lg border border-zinc-200 bg-white p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status === "verified" ? "bg-emerald-50" : "bg-blue-50"}`}>
            <FileText className={`w-5 h-5 ${status === "verified" ? "text-emerald-600" : "text-blue-600"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-zinc-900 truncate">
              {documentName || "Ausweisdokument"}
            </p>
            <p className="text-sm text-zinc-500">Hochgeladen am {formatDate(uploadedAt)}</p>
          </div>
          {status === "verified" && <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
          {status === "uploaded" && <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />}
        </div>
        {status === "uploaded" && <p className="text-xs text-blue-600 mt-2 ml-13">Wird von unserem Team geprüft...</p>}
      </div>;
  }
  return <div className={className}>
      {/* Rejected notice */}
      {status === "rejected" && hasDocument && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          <span>Ihr Dokument wurde abgelehnt. Bitte laden Sie ein neues Dokument hoch.</span>
        </div>}

      <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} role="button" tabIndex={0} aria-label="Ausweisdokument hochladen" aria-disabled={!canUpload} className={`
          relative rounded-lg border-2 border-dashed transition-colors
          ${dragActive ? "border-primary bg-primary/5" : "border-zinc-300 bg-zinc-50"}
          ${canUpload ? "cursor-pointer hover:border-zinc-400" : "cursor-not-allowed opacity-60"}
        `} style={dragActive ? {
      borderColor: tenant.theme.primary
    } : undefined}>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileInput} disabled={!canUpload || isUploading} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />

        <div className="p-6 text-center">
          {isUploading ? <>
              <Loader2 className="w-10 h-10 mx-auto text-zinc-400 animate-spin" />
              <p className="mt-3 text-sm font-medium text-zinc-600">Dokument wird hochgeladen...</p>
            </> : <>
              <Upload className="w-10 h-10 mx-auto text-zinc-400" />
              <p className="mt-3 text-sm font-medium text-zinc-700">Ausweisdokument hochladen</p>
              <p className="mt-1 text-xs text-zinc-500">
                Personalausweis oder Reisepass — PDF, JPG oder PNG, max. 10 MB
              </p>
              <p className="mt-2 text-xs text-zinc-400">
                Ziehen Sie die Datei hierher oder klicken Sie zum Auswählen
              </p>
            </>}
        </div>
      </div>

      {uploadError && <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>}
    </div>;
}